<script lang="ts" setup>
import { DashboardInfo, DashboardInfoArray } from '~/api/dashboard/_types';
import { CurrencyCode } from '~/api/links/_types';
import { t } from '~/plugins/i18n';

const activeCurrency = ref<CurrencyCode>();
const activeCardIndex = ref<number | null>(null);
const activeCard = ref<DashboardInfo | null>(null);

const { data, isLoading, request } = useDashboard();

const dashboardData = computed<DashboardInfoArray | null>(() => {
    if (!data.value?.length) {
        return null;
    }

    if (!activeCurrency.value) {
        activeCurrency.value = data.value[0].Currency;
    }

    return data.value.find((x) => x.Currency === activeCurrency.value)?.Data ?? null;
});

const currencyOptions = computed(() => {
    return data.value?.map((curr) => ({
        label: t(`generic.currency.${curr.Currency}`),
        value: curr.Currency,
    }));
});

const onClickCard = (index: number) => {
    activeCardIndex.value = index;
    activeCard.value = dashboardData.value !== null ? dashboardData.value[activeCardIndex.value] : null;
};

watch(activeCurrency, () => {
    if (activeCardIndex.value !== null) {
        activeCard.value = dashboardData.value !== null ? dashboardData.value[activeCardIndex.value] : null;
    }
});

onMounted(async () => {
    await request();

    if (dashboardData.value) {
        activeCardIndex.value = 0;
        activeCard.value = dashboardData.value[activeCardIndex.value];
    }
});
</script>
<template>
    <div v-if="dashboardData" class="row q-col-gutter-md relative-position" style="min-height: 10rem">
        <div v-if="(currencyOptions?.length ?? 0) > 1" class="col-2 offset-10">
            <q-select
                v-model="activeCurrency"
                :options="currencyOptions"
                :label="$t(`dashboard.select-currency`)"
                bg-color="on-primary"
                map-options
                emit-value
                outlined
                square
                dense
            ></q-select>
        </div>
        <div class="col-12 col-md-6">
            <div class="row q-col-gutter-md" style="height: calc(100% + 16px)">
                <div v-for="(data, index) in dashboardData" :key="data.Type" class="col-6 column q-pa-md">
                    <dashboard-summary-card
                        :data="data"
                        class="full-height"
                        :is-active="activeCardIndex === index"
                        @click="() => onClickCard(index)"
                    />
                </div>
            </div>
        </div>
        <div class="col-12 col-md-6 column q-py-md">
            <dashboard-details-card v-if="activeCard" :data="activeCard" />
        </div>

        <q-inner-loading :showing="isLoading" color="primary" />
    </div>
</template>
